import { tracker } from '@lessonup/analytics';
import { useDocumentMutation } from '@lessonup/client-integration';
import { logger } from '@lessonup/logging-classic';
import { PinPreview } from '@lessonup/pin-renderer';
import { Pin } from '@lessonup/pins-shared';
import {
  AsyncButton,
  BodyText,
  Button,
  elevationShadow,
  IconAdd,
  IconCopy,
  ManagedModal,
  ModalFooter,
  ModalHeaderV1,
  NiceModal,
  NiceModalHocProps,
  rem,
  SpaceBetween,
  spacing,
  styled,
  TextField,
  toast,
  useErrorContext,
  useForm,
  useTranslation,
} from '@lessonup/ui-components';
import { AppError, asError, wait } from '@lessonup/utils';
import { t } from 'i18next';
import React from 'react';
import { copyPinsToClipboard } from '../../../../features/Editor/hooks/useCopyPaste.utils';
import { lessonEditorV2Route } from '../../../../utils/bridge/teacherRoutes';
import { AiToolsStandardGeneratorType, aiToolsTypeToEventType } from '../../AiToolbox.types';
import { i18nextNamespaceAiTools } from '../../AiToolbox.utils';
import { CreateLessonForAiToolsDocument } from './SaveGeneratedOutput.graphql.generated';
import { getPinForGenerator } from './SaveGeneratedOutput.utils';

export const SaveGeneratedOutputButton = (props: SaveOutputModalProps) => {
  const { t } = useTranslation(i18nextNamespaceAiTools);
  const modal = NiceModal.useModal(SaveOutputModal);

  const handleOpenModal = () => {
    tracker.events.aiToolsUseInLessonModal({
      aiToolType: aiToolsTypeToEventType[props.generatorType],
    });
    modal.show(props);
  };

  return (
    <Button onClick={handleOpenModal} iconStart={<IconAdd />}>
      {t('saveToNewLessonButton')}
    </Button>
  );
};

interface SaveOutputModalProps {
  generatorType: AiToolsStandardGeneratorType;
  html: string;
  slideTitle?: string;
}

export const SaveOutputModal: React.FC<SaveOutputModalProps & NiceModalHocProps> = NiceModal.create(
  ({ html, slideTitle, generatorType }) => {
    const modal = NiceModal.useModal();

    function closeModal() {
      modal.hide();
    }
    return (
      <ManagedModal modal={modal} contentLabel={t(`modalLabel`)}>
        <SaveOutputModalInner
          html={html}
          closeModal={closeModal}
          slideTitle={slideTitle}
          generatorType={generatorType}
        />
      </ManagedModal>
    );
  }
);

interface SaveOutputModalInnerProps {
  html: string;
  closeModal: () => void;
  slideTitle?: string;
  generatorType: AiToolsStandardGeneratorType;
}

interface NewLessonFormData {
  name: string;
  slideTitle: string;
}

const SaveOutputModalInner = ({
  html,
  closeModal,
  slideTitle: initialSlideTitle,
  generatorType,
}: SaveOutputModalInnerProps) => {
  const { t } = useTranslation(i18nextNamespaceAiTools, { keyPrefix: 'saveToNewLesson' });
  const { setError } = useErrorContext();

  const { register, handleSubmit, watch } = useForm<NewLessonFormData>({
    defaultValues: {
      name: t('defaultLessonName'),
      slideTitle: initialSlideTitle ?? t(`defaultSlideTitle`),
    },
  });
  const slideTitle = watch('slideTitle');

  const [createLesson] = useDocumentMutation(CreateLessonForAiToolsDocument, {});
  const pin = getPinForGenerator(slideTitle, html);

  const onSaveToNewLesson = async ({ name }: NewLessonFormData) => {
    try {
      const response = await createLesson({
        variables: {
          input: {
            pins: [pin],
            name,
          },
        },
      });
      if (response.data?.createLesson.lesson.id) {
        tracker.events.aiToolsUseInLessonCreateNewLesson({
          aiToolType: aiToolsTypeToEventType[generatorType],
        });
        window.location.href = lessonEditorV2Route.href({ lessonId: response.data?.createLesson.lesson.id });
        // Wait for the lesson to be loaded, 5000 ms is enough time for the lesson to be loaded,
        // and short enough to not lock the user if nothing happens
        await wait(5000);
      } else {
        throw new AppError('unexpected-data', 'Failed to create lesson');
      }
    } catch (error) {
      logger.error(asError(error));
      setError({ error: asError(error) });
    }
  };

  const onCopySlide = () => {
    try {
      copyPinsToClipboard([pin]);
      toast({
        title: t('copySlideSuccess'),
        message: t('copySlideSuccessMessage'),
      });
      tracker.events.aiToolsUseInLessonCopySlide({
        aiToolType: aiToolsTypeToEventType[generatorType],
      });
    } catch (error) {
      logger.error(asError(error));
      setError({ error: asError(error) });
    }
  };

  return (
    <>
      <ModalHeaderV1 type="headline" title={{ content: t('modalTitle'), size: 'M' }} onCloseButtonClick={closeModal} />
      <ModalContent>
        <SpaceBetween direction="y" spacing={spacing.size32}>
          <TextField
            size="medium"
            type="text"
            label={t('slideTitleLabel')}
            {...register('slideTitle', {
              required: true,
            })}
          />
          <StyledPinPreview pin={pin as unknown as Pin} />
          <BodyText>{t('instructions')}</BodyText>

          <TextField
            size="medium"
            type="text"
            label={t('newLessonLabel')}
            maxLength={300}
            {...register('name', {
              required: true,
            })}
          />
        </SpaceBetween>
      </ModalContent>
      <ModalFooter type="withChildren">
        <Button buttonType="secondary" iconStart={<IconCopy />} onClick={onCopySlide}>
          {t('copySlideButton')}
        </Button>
        <AsyncButton iconStart={<IconAdd />} onClick={handleSubmit(onSaveToNewLesson)}>
          {t('saveButton')}
        </AsyncButton>
      </ModalFooter>
    </>
  );
};

const StyledPinPreview = styled(PinPreview)`
  margin: ${spacing.size16} auto;
  max-width: 500px;
  box-shadow: ${elevationShadow.e100};
`;

const ModalContent = styled(BodyText)`
  padding: ${spacing.size16};
  max-width: ${rem('560px')};
`;
